import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Person,
  Event,
  AdminPanelSettings,
  DeleteOutline,
} from "@mui/icons-material";
import { deleteHoliday, isAuthenticated } from "../../../ApiService/ApiService";
import { formatDateOutput } from "../../../Utils/formatDate";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  { id: "name", label: "Name", icon: <Person sx={{ marginRight: 1 }} /> },
  {
    id: "startDate",
    label: "Start Date",
    icon: <Event sx={{ marginRight: 1 }} />,
  },
  { id: "endDate", label: "End Date", icon: <Event sx={{ marginRight: 1 }} /> },
  {
    id: "actions",
    label: "Actions",
    icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
  },
];

function HolidaysTable({
  holidays,
  selectedSessionId,
  fetchHolidaysBySessionId,
}) {
  const [loading, setLoading] = useState(false);
  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(holidays?.data);
  }, [holidays]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleDeleteHolidayById = async (holidayId) => {
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await deleteHoliday(holidayId);
        // console.log("RES", response?.data);
        if (response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Holiday deleted successfully",
            buttons: [{ name: "OK", onPress: handleDialogClose }],
          });
          fetchHolidaysBySessionId(selectedSessionId)
            .then(() => setLoading(false))
            .catch(() => {
              setLoading(false);
              setDialog({
                visible: true,
                title: "Error",
                message: "Failed to fetch holidays",
                buttons: [{ name: "Close", onPress: handleDialogClose }],
              });
            });
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.startDate)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.endDate)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteHolidayById(row?.id)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

HolidaysTable.propTypes = {
  holidays: PropTypes.object.isRequired,
  selectedSessionId: PropTypes.string.isRequired,
  fetchHolidaysBySessionId: PropTypes.func.isRequired,
};

export default HolidaysTable;
